// data.service.ts

import {HttpClient, HttpHeaders } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {environment} from 'src/environments/environment';
import {Product} from '../shared/models/product'; // Asegúrate de que esta es la interfaz correcta y unificada
import { Order } from '../shared/models/order';
import { Dates } from '../shared/models/order';
import {API_ENDPOINT} from '../shared/Constant/constant';
import {HttpParams} from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  public baseUrl = environment.apiUrl;
  public search = new BehaviorSubject<string>("");

  public $product = new BehaviorSubject<Product[]>(this.getCartFromSessionStorage()); // Usa Product[] directamente

  private cartKey = 'cartItems';

  private orderSource = new BehaviorSubject<Order | null>(null);
  currentOrder = this.orderSource.asObservable();

  constructor(private http: HttpClient) {
    // Emit the current cart from localStorage immediately so UI shows persisted items
    const localCart = this.getCartFromSessionStorage();
    this.$product.next(localCart);

    // If there's something in local storage, validate against server and subscribe so request runs
    if (localCart && localCart.length > 0) {
      this.validateCartWithServer().subscribe((updated) => {
        // Only update the subject if the validation returned a non-empty array
        // (validateCartWithServer already writes to localStorage and emits when appropriate)
        if (updated && updated.length > 0) {
          this.$product.next(updated);
        }
      });
    }
  }

  // --- Métodos de Ordenes y Carga de Archivos (sin cambios significativos) ---

  setOrder(order: Order): void {
    this.orderSource.next(order);
  }

  searchOrders(user: string, email: string): Observable<Order[]> {
    const url = `${this.baseUrl}${API_ENDPOINT.ORDER_SEARCH}?user=${user}&email=${encodeURIComponent(email)}`;
    return this.http.get<Order[]>(url);
  }

  createOrder(order: Order): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(`${this.baseUrl}${API_ENDPOINT.ORDER}`, order, { headers });
  }

  getMaxDatesOrder(): Observable<any> {
    return this.http.get<Dates>(`${this.baseUrl}${API_ENDPOINT.DATES}`)
  }

  uploadImage(file: File, origin: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    formData.append('origin',origin)

    return this.http.post(`${this.baseUrl}${API_ENDPOINT.UPLOADURL}`, formData);
  }

  // --- Métodos de Productos (sin cambios significativos) ---

  getAllProduct(offset: number, limit: number): Observable<any> {
    const params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString());
    return this.http.get<Product>(`${this.baseUrl}${API_ENDPOINT.PRODUCTS}`, {params})
  }

  getAllProductList(): Observable<any> {
    return this.http.get<Product>(`${this.baseUrl}${API_ENDPOINT.PRODUCTS_LIST}`)
  }

  getAllCategory(): Observable<any> {
    return this.http.get(`${this.baseUrl}${API_ENDPOINT.PRODUCTS_CATEGORIES}`);
  }

  getPriceRange(minPrice: number, maxPrice: number): Observable<any> {
    const params = new HttpParams()
      .set('minPrice', minPrice)
      .set('maxPrice', maxPrice);
    return this.http.get(`${this.baseUrl}${API_ENDPOINT.PRODUCTS_BY_PRICE_RANGE}`, {params});
  }

  getInCategory(type: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${API_ENDPOINT.PRODUCTS_CATEGORIES_TYPE}${type}`);
  }

  getSingleProduct(id: number) {
    return this.http.get(`${this.baseUrl}${API_ENDPOINT.PRODUCTS}${id}`);
  }

  // --- Métodos del Carrito de Compras ---

  private getCartFromSessionStorage(): Product[] {
    try {
      const cart = localStorage.getItem(this.cartKey);
      return cart ? JSON.parse(cart) : [];
    } catch (e) {
      console.error("Error parsing cart from sessionStorage", e);
      return [];
    }
  }

  getProductsCount(): Observable<Product[]> { // Usa Product[] aquí
    return this.$product.asObservable();
  }

  getCart(): Product[] {
    return this.$product.getValue();
  }

  getTotalPrice(): number {
    const currentCart = this.$product.getValue();
    return currentCart.reduce((total: number, item: any) => {
      return total + (item.selectedSize?.price !== undefined ? item.selectedSize.price * (item.quantity || 1) : item.price || 0);
    }, 0);
  }

  addProductToCart(item: Product): void {
    let currentCart = this.getCartFromSessionStorage();

    const existingItemIndex = currentCart.findIndex(cartItem =>
        cartItem.idTable === item.idTable &&
        cartItem.selectedColor?.name === item.selectedColor?.name &&
        cartItem.selectedSize?.size === item.selectedSize?.size
    );

    if (existingItemIndex > -1) {
        // **CORRECCIÓN AQUÍ:** Aseguramos que 'quantity' es un número antes de sumar
        const existingItem = currentCart[existingItemIndex];
        // Inicializa quantity a 0 si es undefined, luego suma
        existingItem.quantity = (existingItem.quantity || 0) + (item.quantity || 1);
    } else {
        // Asegúrate de que el 'item' que se añade tenga una cantidad inicial, si no la tiene
        // Esto es importante para que los nuevos items también tengan 'quantity' definida.
        if (item.quantity === undefined || item.quantity === null) {
            item.quantity = 1; // O el valor por defecto que desees
        }
        currentCart.push(item);
    }

    localStorage.setItem(this.cartKey, JSON.stringify(currentCart));
    this.$product.next(currentCart);
  }

  updateCartItem(updatedItem: Product): void { // Usa Product aquí
    let currentCart = this.getCartFromSessionStorage();
    const index = currentCart.findIndex((item: Product) => item.idTable === updatedItem.idTable); // Usa Product[] en el tipo de item
    if (index !== -1) {
      currentCart[index] = updatedItem;
      localStorage.setItem(this.cartKey, JSON.stringify(currentCart));
      this.$product.next(currentCart);
    }
  }

  removeItemFromCheckOut(productId: string): void {
    let currentCart = this.getCartFromSessionStorage();
    const updatedCart = currentCart.filter((item: Product) => item.idTable !== productId); // Usa Product[] en el tipo de item
    localStorage.setItem(this.cartKey, JSON.stringify(updatedCart));
    this.$product.next(updatedCart);
  }

  clearCart(): void {
    localStorage.removeItem(this.cartKey);
    this.$product.next([]);
  }

  // Validate cart items against server product list: update prices/stock or remove missing items
  validateCartWithServer(): Observable<Product[]> {
    try {
      const localCart = this.getCartFromSessionStorage();
      if (!localCart || localCart.length === 0) {
        return of(localCart); // nothing to validate, return current local cart
      }

      // Fetch full product list (uses existing method)
      return this.getAllProductList().pipe(
        map((products: any[]) => {
          // If server returned nothing, keep local cart unchanged
          if (!products || products.length === 0) {
            return localCart;
          }

          const productsMap = new Map<string, any>();
          products.forEach((p: any) => {
            // Ensure id key is string
            productsMap.set(String(p.id), p);
          });

          const updatedCart: Product[] = [];

          for (const cartItem of localCart) {
            const prod = productsMap.get(String(cartItem.id));
            if (!prod) {
              // Product no longer available -> skip (remove from cart)
              continue;
            }

            // Resolve selected size from server product
            let serverSize: any = null;
            if (cartItem.selectedSize) {
              const sel = cartItem.selectedSize as any;
              serverSize = (prod.sizePriceLts || []).find((s: any) => {
                return (sel.id && s.id === sel.id) || (sel.size && s.size === sel.size);
              });
            }
            if (!serverSize) {
              // fallback to first available size
              serverSize = (prod.sizePriceLts && prod.sizePriceLts.length > 0) ? prod.sizePriceLts[0] : null;
            }

            if (!serverSize) {
              // No sizes available -> skip product
              continue;
            }

            // Adjust quantity if stock changed
            const requestedQty = Number(cartItem.quantity || 1);
            const availableStock = Number(serverSize.stock || 0);
            const finalQty = Math.min(requestedQty, availableStock);
            if (finalQty <= 0) {
              // out of stock -> skip
              continue;
            }

            // Build updated cart item using server authoritative fields
            const updatedItem: Product = {
              ...prod,
              idTable: cartItem.idTable || prod.idTable,
              selectedSize: {
                ...serverSize
              },
              selectedColor: cartItem.selectedColor || prod.selectedColor,
              quantity: finalQty,
              personalized: cartItem.personalized || false
            } as Product;

            updatedCart.push(updatedItem);
          }

          // Save updated cart and update BehaviorSubject only if we built something different
          // (avoid overwriting with empty array if server data was not useful)
          localStorage.setItem(this.cartKey, JSON.stringify(updatedCart));
          this.$product.next(updatedCart);

          return updatedCart;
        }),
        catchError((err) => {
          console.error('Error fetching products to validate cart', err);
          // On error, keep and return the local cart instead of clearing it
          return of(localCart);
        })
      );
    } catch (e) {
      console.error('Error validating cart from localStorage', e);
      return of(this.getCartFromSessionStorage());
    }
  }
}